import { Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormRecord, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DropdownData,
  GenericDropdownComponent,
  CurrencyInputFieldComponent,
  rxResourceFixed
} from '@zayan-erp/shared';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { InputNumber } from '@openng/optimus-ui/inputnumber';
import { Message } from '@openng/optimus-ui/message';
import { map, NEVER, of } from 'rxjs';
import {
  DsmOptionConfigForm,
  DsmProductAttributeGroup,
  DsmProductOption
} from '../../../models/dsm-deal-options.models';
import { DsmModelElevationProductsService } from '../../../services/dsm-model-elevation-products.service';
import { LocationItem } from '@zayan-erp/home-building';

@Component({
  selector: 'dsm-option-config-fields',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    GenericDropdownComponent,
    CurrencyInputFieldComponent,
    InputNumber,
    TextareaModule,
    Message,
    ButtonModule
  ],
  templateUrl: './dsm-option-config-fields.component.html'
})
export class DsmOptionConfigFieldsComponent {
  readonly #modelElevationProductsService = inject(DsmModelElevationProductsService);
  readonly #destroyRef = inject(DestroyRef);

  readonly addendumId = input<string | null>(null);
  // Location source before an addendum exists (appointment screens); ignored when addendumId is set.
  readonly lotId = input<string | null>(null);
  readonly product = input.required<DsmProductOption>();
  readonly form = input.required<DsmOptionConfigForm>();
  readonly attributeGroups = input<DsmProductAttributeGroup[]>([]);
  readonly attributesLoaded = input<boolean>(false);
  readonly submitAttempted = input<boolean>(false);
  readonly requireQuantity = input<boolean>(false);
  readonly requireUnitPrice = input<boolean>(false);
  // Preselected attribute values keyed by subCategoryAttributeId (used when editing).
  readonly initialAttributeValues = input<Record<string, string>>({});
  // Supplied by a kit parent that already fetched all its kit-product-locations in one call:
  // null while the parent is still loading, undefined when used standalone (self-fetch).
  readonly preloadedLocations = input<LocationItem[] | null>();

  readonly showCopyToLinked = input<boolean>(false);
  // Whether this fields block is on the currently visible tab (parent keeps it in the DOM when tab-switched).
  readonly active = input<boolean>(true);
  readonly copyLocationToLinked = output<void>();
  readonly copyQuantityToLinked = output<void>();
  readonly copyAttributesToLinked = output<string>();

  readonly #locationApplied = signal(false);
  readonly locationApplied = this.#locationApplied.asReadonly();

  readonly #quantityApplied = signal(false);
  readonly quantityApplied = this.#quantityApplied.asReadonly();

  readonly #appliedAttributeIds = signal<ReadonlySet<string>>(new Set());
  readonly appliedAttributeIds = this.#appliedAttributeIds.asReadonly();

  get attributeValuesGroup(): FormRecord<FormControl<string | null>> {
    return this.form().controls.attributeValues;
  }

  readonly hasAttributeConfigError = computed(
    () => this.product().selectionRequired && this.attributesLoaded() && this.attributeGroups().length === 0
  );

  readonly #fieldsVersion = signal(0);

  readonly hasCopyableLocation = computed(() => {
    this.#fieldsVersion();
    return !!this.form().controls.locationId.value;
  });

  readonly hasCopyableQuantity = computed(() => {
    this.#fieldsVersion();
    return !!this.form().controls.quantity.value;
  });

  readonly copyableAttributeIds = computed(() => {
    this.#fieldsVersion();
    const ids = new Set<string>();
    Object.entries(this.form().controls.attributeValues.controls).forEach(([id, control]) => {
      if (control.value) ids.add(id);
    });
    return ids;
  });

  readonly locationOptions = rxResourceFixed({
    params: () => ({
      productId: this.product().productId,
      addendumId: this.addendumId(),
      lotId: this.lotId(),
      preloaded: this.preloadedLocations()
    }),
    defaultValue: [] as DropdownData[],
    stream: ({ params }) => {
      if (params.preloaded === null) return NEVER;
      if (params.preloaded)
        return of(
          params.preloaded.map(location => ({
            id: location.id,
            firstLabel: location.locationCode ?? '—',
            secondLabel: location.locationName
          }))
        );
      if (!params.productId) return of<DropdownData[]>([]);
      return this.#modelElevationProductsService.getScopedProductLocations(params, params.productId).pipe(
        map(locations =>
          locations.map(location => ({
            id: location.id,
            firstLabel: location.locationCode ?? '—',
            secondLabel: location.locationName
          }))
        )
      );
    }
  });

  readonly attributeValueOptions = computed(() => {
    const optionsMap = new Map<string, DropdownData[]>();
    this.attributeGroups().forEach(attribute => {
      optionsMap.set(
        attribute.subCategoryAttributeId,
        attribute.productAttributeValues.map(value => ({ id: value.id, firstLabel: value.value }))
      );
    });
    return optionsMap;
  });

  constructor() {
    // Re-evaluate hasCopyableValues whenever the form's values change.
    effect(() => {
      const form = this.form();
      form.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => this.#fieldsVersion.update(v => v + 1));
    });

    // Location was re-picked after being applied: the "Applied" state no longer reflects the field.
    effect(() => {
      const form = this.form();
      form.controls.locationId.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => this.#locationApplied.set(false));
    });

    // Quantity was re-entered after being applied: the "Applied" state no longer reflects the field.
    effect(() => {
      const form = this.form();
      form.controls.quantity.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => this.#quantityApplied.set(false));
    });

    // Keep dynamic attribute controls in sync with the supplied attribute groups.
    effect(() => {
      const attributes = this.attributeGroups();
      const formRecord = this.attributeValuesGroup;
      const controls = formRecord.controls;
      const selectionRequired = this.product().selectionRequired;
      const attributeIds = new Set(attributes.map(attribute => attribute.subCategoryAttributeId));

      const initialValues = this.initialAttributeValues();

      attributes.forEach(attribute => {
        if (!controls[attribute.subCategoryAttributeId]) {
          const control = new FormControl<string | null>(initialValues[attribute.subCategoryAttributeId] ?? null, {
            validators: selectionRequired ? [Validators.required] : []
          });
          if (this.submitAttempted()) {
            control.markAsTouched();
          }
          // Attribute value was re-picked after being applied: drop its "Applied" state.
          control.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
            this.#appliedAttributeIds.update(ids => {
              if (!ids.has(attribute.subCategoryAttributeId)) return ids;
              const next = new Set(ids);
              next.delete(attribute.subCategoryAttributeId);
              return next;
            });
          });
          formRecord.addControl(attribute.subCategoryAttributeId, control);
        }
      });

      Object.keys(controls).forEach(controlName => {
        if (!attributeIds.has(controlName)) {
          formRecord.removeControl(controlName);
        }
      });
    });

    // A required selection with no configurable attributes makes the option un-configurable.
    effect(() => {
      const form = this.form();
      if (this.hasAttributeConfigError()) {
        form.setErrors({ ...(form.errors ?? {}), attributeConfigError: true });
      } else if (form.errors?.['attributeConfigError']) {
        const { attributeConfigError, ...rest } = form.errors;
        form.setErrors(Object.keys(rest).length ? rest : null);
      }
    });

    // Leaving/re-entering this tab clears any "Applied" button state.
    effect(() => {
      this.active();
      this.#locationApplied.set(false);
      this.#quantityApplied.set(false);
      this.#appliedAttributeIds.set(new Set());
    });
  }

  onCopyLocationToLinked(): void {
    this.copyLocationToLinked.emit();
    this.#locationApplied.set(true);
  }

  onCopyQuantityToLinked(): void {
    this.copyQuantityToLinked.emit();
    this.#quantityApplied.set(true);
  }

  onCopyAttributeToLinked(attributeId: string): void {
    this.copyAttributesToLinked.emit(attributeId);
    this.#appliedAttributeIds.update(ids => new Set(ids).add(attributeId));
  }
}
